/**
 * MessageBubble
 * Shared message bubble used by DM and Ritual Group chat areas.
 * Handles sent/received styling, sender info, deleted state, and action menu.
 */

import { MoreVertical, Pencil, Trash2 } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router-dom"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { renderMessageContent } from "../../utils/renderMessageContent"

interface MessageBubbleProps {
  content: string
  isOwn: boolean
  isDeleted?: boolean
  deletedLabel?: string
  /** Sender info shown below received messages */
  senderId?: string | null
  senderName?: string | null
  senderPhoto?: string | null
  /** Menu actions */
  onEdit?: () => void
  onDelete?: () => void
  /** Labels for menu items */
  editLabel?: string
  deleteLabel?: string
  confirmDeleteLabel?: string
  /** Show menu for all messages (moderation) vs only own */
  showMenuForAll?: boolean
  /** Show sender avatar + name below received messages. Defaults to true. */
  showSenderInfo?: boolean
}

export function MessageBubble({
  content,
  isOwn,
  isDeleted,
  deletedLabel = "This message was deleted",
  senderId,
  senderName,
  senderPhoto,
  onEdit,
  onDelete,
  editLabel = "Edit",
  deleteLabel = "Delete",
  confirmDeleteLabel = "Confirm delete",
  showMenuForAll = false,
  showSenderInfo = true,
}: MessageBubbleProps) {
  const [confirmDelete, setConfirmDelete] = useState(false)

  if (isDeleted) {
    return (
      <div className={cn("flex", isOwn ? "justify-end" : "justify-start")}>
        <div className="max-w-[500px] rounded-[10px] p-[15px] bg-[var(--color-chat-border)]">
          <p className="text-[14px] text-muted-foreground italic">{deletedLabel}</p>
        </div>
      </div>
    )
  }

  const handleDelete = () => {
    if (confirmDelete) {
      onDelete?.()
      setConfirmDelete(false)
    } else {
      setConfirmDelete(true)
    }
  }

  const showMenu = showMenuForAll || isOwn

  return (
    <div className={cn("flex group", isOwn ? "justify-end" : "justify-start")}>
      <div className="max-w-[500px]">
        <div className="relative">
          <div
            className={cn(
              "rounded-[10px] p-[15px] text-[14px] leading-[22px] text-foreground",
              isOwn ? "bg-[var(--color-brand-slate)]" : "bg-[var(--color-brand-olive)]",
            )}
          >
            <div className="whitespace-pre-wrap break-words">{renderMessageContent(content)}</div>
          </div>

          {showMenu && (onEdit || onDelete) && (
            <div className="absolute -top-8 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <DropdownMenu
                onOpenChange={open => {
                  if (!open) setConfirmDelete(false)
                }}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="bg-popover border border-border shadow-md"
                  >
                    <MoreVertical className="size-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-36">
                  {isOwn && onEdit && (
                    <DropdownMenuItem onClick={onEdit}>
                      <Pencil />
                      {editLabel}
                    </DropdownMenuItem>
                  )}
                  {onDelete && (
                    <DropdownMenuItem variant="destructive" onClick={handleDelete}>
                      <Trash2 />
                      {confirmDelete ? confirmDeleteLabel : deleteLabel}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {/* Sender avatar + name below received messages */}
        {showSenderInfo &&
          !isOwn &&
          senderName &&
          (senderId ? (
            <Link
              to={`/profile/${senderId}`}
              className="flex items-center gap-2 mt-2 ml-1 no-underline hover:opacity-80 transition-opacity"
            >
              <Avatar
                src={senderPhoto || undefined}
                alt={senderName}
                fallback={senderName}
                size="sm"
              />
              <span className="text-[13px] font-semibold text-foreground">{senderName}</span>
            </Link>
          ) : (
            <div className="flex items-center gap-2 mt-2 ml-1">
              <Avatar
                src={senderPhoto || undefined}
                alt={senderName}
                fallback={senderName}
                size="sm"
              />
              <span className="text-[13px] font-semibold text-foreground">{senderName}</span>
            </div>
          ))}
      </div>
    </div>
  )
}
